import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Card, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import NewLeadsTable from "./new_leads_detail/newLeadsTable";
import {
  newLeadsAction,
  getAllNewLeadsAsync,
  importNewLeadsAsync,
  getCreatedByUsersAsync,
  getBatchNamesAsync,
  downloadSampleExcelAsync,
} from "../redux";
import Select from "react-select";
import { useIsAccessAllow } from "../../users/redux";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { Tooltip, OverlayTrigger, Modal, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./newLeads.scss";

export const NewDataCall = () => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const fromDateContainerRef = useRef(null);
  const toDateContainerRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState(null);
  const [batchNames, setBatchNames] = useState([]);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [createdByUsers, setCreatedByUsers] = useState([]);
  const [isLoadingCreatedByUsers, setIsLoadingCreatedByUsers] = useState(false);

  const { refreshColdLeadsList, searchBy, searchText, audioType, batchFilter, fromDateFilter, toDateFilter, leadType, callOutcomeFilter, companyNameFilter, createdByFilter } = useSelector(
    (state) => state.newDataCall,
    shallowEqual
  );
  const { user } = useSelector((state) => state.auth, shallowEqual);

  // Same rule as getAllNewLeadsAsync: non-admins (except the listed email) only see their own leads, so they do not get this filter
  // eslint-disable-next-line eqeqeq
  const restrictCreatedByToSelf =
    user && user._id && user.roleId != 1 && user.email != "himanshu.soni@webcluesinfotech.com";
  const showAllCreatedByFilter = !restrictCreatedByToSelf;

  useEffect(() => {
    loadBatchNames();
    dispatch(newLeadsAction.resetRefreshFlag());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showAllCreatedByFilter) {
      loadCreatedByUsers();
    }
  }, [showAllCreatedByFilter]);

  useEffect(() => {
    if (restrictCreatedByToSelf) {
      dispatch(newLeadsAction.setCreatedByFilter(""));
    }
  }, [restrictCreatedByToSelf, dispatch]);

  useEffect(() => {
    if (!showAllCreatedByFilter) {
      return;
    }
    loadBatchNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdByFilter, showAllCreatedByFilter]);

  const loadBatchNames = async () => {
    setIsLoadingBatches(true);
    try {
      const result = await dispatch(getBatchNamesAsync());
      if (result?.success && result.data) {
        setBatchNames(Array.isArray(result.data) ? result.data : []);
      } else {
        setBatchNames([]);
      }
    } catch (error) {
      console.error("Failed to load batch names", error);
      setBatchNames([]);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (refreshColdLeadsList) {
      dispatch(getAllNewLeadsAsync(searchBy, searchText, audioType, batchFilter, fromDateFilter, toDateFilter, leadType, createdByFilter, "createdOn", "desc", callOutcomeFilter, companyNameFilter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshColdLeadsList, searchBy, searchText, audioType, batchFilter, fromDateFilter, toDateFilter, leadType, callOutcomeFilter, companyNameFilter, createdByFilter]);

  useEffect(() => {
    const datePickerInput = fromDateContainerRef.current?.querySelector('input');
    if (datePickerInput) {
      const handleFocus = () => {
        fromDateContainerRef.current?.classList.add('date-picker-focused');
      };
      const handleBlur = () => {
        fromDateContainerRef.current?.classList.remove('date-picker-focused');
      };
      datePickerInput.addEventListener('focus', handleFocus);
      datePickerInput.addEventListener('blur', handleBlur);
      return () => {
        datePickerInput.removeEventListener('focus', handleFocus);
        datePickerInput.removeEventListener('blur', handleBlur);
      };
    }
  }, [fromDateFilter]);

  useEffect(() => {
    const datePickerInput = toDateContainerRef.current?.querySelector('input');
    if (datePickerInput) {
      const handleFocus = () => {
        toDateContainerRef.current?.classList.add('date-picker-focused');
      };
      const handleBlur = () => {
        toDateContainerRef.current?.classList.remove('date-picker-focused');
      };
      datePickerInput.addEventListener('focus', handleFocus);
      datePickerInput.addEventListener('blur', handleBlur);
      return () => {
        datePickerInput.removeEventListener('focus', handleFocus);
        datePickerInput.removeEventListener('blur', handleBlur);
      };
    }
  }, [toDateFilter]);

  const loadCreatedByUsers = async () => {
    setIsLoadingCreatedByUsers(true);
    try {
      const result = await dispatch(getCreatedByUsersAsync());
      if (result?.success && result.data) {
        const users = Array.isArray(result.data) ? result.data : [];
        setCreatedByUsers(users);
      } else {
        setCreatedByUsers([]);
      }
    } catch (error) {
      console.error("Failed to load created by users", error);
      setCreatedByUsers([]);
    } finally {
      setIsLoadingCreatedByUsers(false);
    }
  };

  const onSearchTextChange = (event) => {
    dispatch(newLeadsAction.searchTextChange(event.target.value));
  };

  const toBatchName = (batch) => {
    if (typeof batch === "string" && batch) return batch;
    if (batch && typeof batch === "object" && batch.name) return String(batch.name);
    return "";
  };

  const batchOptions = [
    { value: "", label: "All Batches" },
    ...(batchNames || [])
      .map((batch) => {
        const name = toBatchName(batch);
        return name ? { value: name, label: name } : null;
      })
      .filter(Boolean),
  ];

  const defaultBatch = batchOptions.find((option) => option.value === batchFilter) || batchOptions[0];

  const changeBatchFilter = (option) => {
    dispatch(newLeadsAction.setBatchFilter(option.value));
  };

  const audioTypeOptions = [
    { value: "all", label: "All Data" },
    { value: "with_audio", label: "With Audio" },
    { value: "with_out_audio", label: "Without Audio" },
  ];

  const defaultAudioType =
    audioTypeOptions.find((option) => option.value === audioType) || audioTypeOptions[0];

  const changeAudioType = (option) => {
    dispatch(newLeadsAction.setAudioType(option.value));
  };

  const leadTypeOptions = [
    { value: "all", label: "All Leads" },
    { value: "Blocked", label: "Blocked" },
    { value: "Unblock", label: "Unblock" },
  ];

  const defaultLeadType = leadTypeOptions.find((option) => option.value === leadType) || leadTypeOptions[0];

  const changeLeadType = (option) => {
    dispatch(newLeadsAction.setLeadType(option.value));
  };

  const callOutcomeOptions = [
    { value: "all", label: "All Outcomes" },
    { value: "Call Concluded", label: "Call Concluded" },
    { value: "Not Concluded", label: "Not Concluded" },
    { value: "Not Interested", label: "Not Interested" },
    { value: "Interested in future", label: "Interested in future" },
  ];

  const defaultCallOutcome = callOutcomeOptions.find((option) => option.value === callOutcomeFilter) || callOutcomeOptions[0];

  const changeCallOutcomeFilter = (option) => {
    dispatch(newLeadsAction.setCallOutcomeFilter(option.value));
  };

  const companyNameOptions = [
    { value: "all", label: "All Companies" },
    { value: "webclues", label: "Webclues" },
    { value: "codezeros", label: "Codezeros" },
  ];

  const defaultCompanyName =
    companyNameOptions.find((option) => option.value === companyNameFilter) || companyNameOptions[0];

  const changeCompanyNameFilter = (option) => {
    dispatch(newLeadsAction.setCompanyNameFilter(option.value));
  };

  const createdByOptions = [
    { value: "", label: "All Created By" },
    ...(createdByUsers || []).map((user) => ({
      value: user._id,
      label: user.name,
    })),
  ];

  const defaultCreatedBy = createdByOptions.find((option) => option.value === createdByFilter) || createdByOptions[0];

  const changeCreatedByFilter = (option) => {
    dispatch(newLeadsAction.setCreatedByFilter(option.value));
  };

  // Format date to DD/MM/YYYY for API
  const formatDateForAPI = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse date from DD/MM/YYYY string
  const parseDateFromAPI = (dateStr) => {
    if (!dateStr) return null;
    // Handle DD/MM/YYYY format
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    // Fallback to default Date parsing for other formats
    return new Date(dateStr);
  };

  const handleFromDateChange = (date) => {
    const formattedDate = date ? formatDateForAPI(date) : "";
    dispatch(newLeadsAction.setFromDateFilter(formattedDate));
    // If new From Date is after the currently selected To Date, clear To Date
    if (date && toDateFilter) {
      const currentToDate = parseDateFromAPI(toDateFilter);
      if (currentToDate && date > currentToDate) {
        dispatch(newLeadsAction.setToDateFilter(""));
      }
    }
  };

  const handleToDateChange = (date) => {
    // Prevent selecting a To Date that is before the From Date
    if (date && fromDateFilter) {
      const currentFromDate = parseDateFromAPI(fromDateFilter);
      if (currentFromDate && date < currentFromDate) {
        return; // Ignore the invalid selection
      }
    }
    const formattedDate = date ? formatDateForAPI(date) : "";
    dispatch(newLeadsAction.setToDateFilter(formattedDate));
  };

  const handleDownloadSample = async () => {
    await dispatch(downloadSampleExcelAsync());
  };

  const handleImportClick = () => {
    if (!companyNameFilter || companyNameFilter === "all") {
      dispatch(
        showSuccessSnackbar(
          "error",
          "Please select Webclues or Codezeros in the All Companies dropdown before importing data.",
          7000
        )
      );
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];
    const isValidType = validTypes.includes(file.type) || file.name.match(/\.(xlsx|xls)$/i);

    if (!isValidType) {
      // Show error message for invalid file type
      dispatch(showSuccessSnackbar("error", "Unsupported file type. Please upload an Excel file.", 5000));
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (!companyNameFilter || companyNameFilter === "all") {
      dispatch(
        showSuccessSnackbar(
          "error",
          "Please select Webclues or Codezeros in the All Companies dropdown before importing data.",
          5000
        )
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setIsImporting(true);
    try {
      const result = await dispatch(importNewLeadsAsync(file));
      const details = result?.details;
      if (
        !result?.success &&
        details &&
        (details.fileDuplicates?.length || details.dbDuplicates?.length || details.missingRequiredRows?.length)
      ) {
        setImportErrors({ message: result.error, ...details });
      }
      if (result?.success) {
        loadBatchNames();
        dispatch(getAllNewLeadsAsync(searchBy, searchText, audioType, batchFilter, fromDateFilter, toDateFilter, leadType, createdByFilter, "createdOn", "desc", callOutcomeFilter, companyNameFilter));
      }
    } catch (error) {
      console.error("Import error:", error);
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const isSearchAllow = useIsAccessAllow("new_data_call.list");
  const isImportAllow = useIsAccessAllow("new_data_call.import");

  return (
    <Card style={{ background: "#ffffff", borderRadius: 16, boxShadow: "0 15px 40px rgba(15, 23, 42, 0.08)", border: "1px solid #e5e7eb", overflow: "hidden" }}>
      <CardHeader
        title={
          <span style={{ fontSize: "20px", fontWeight: 600 }}>New Data Call</span>
        }
      >
        <CardHeaderToolbar>
          <div className="d-flex flex-wrap ap_filter_wraper justify-content-end align-items-end gap-1 margin-left-auto" style={{ position: 'relative', zIndex: 10 }}>
            <form className="subheader_filter" onSubmit={(event) => event.preventDefault()}>
              <div className="form-row">
                <div className="form-group mr-3 mb-4 p-0" style={{ width: "150px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="All Batches"
                    className="custom-react-select"
                    options={batchOptions}
                    onChange={changeBatchFilter}
                    value={defaultBatch}
                    isLoading={isLoadingBatches}
                  />
                </div>
                <div className="form-group mr-3 mb-4 p-0" style={{ width: "200px", marginRight: "50px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="Select audio type"
                    className="custom-react-select"
                    options={audioTypeOptions}
                    onChange={changeAudioType}
                    value={defaultAudioType}
                  />
                </div>
                {isSearchAllow && (
                  <div className="form-group mr-3 mb-4">
                    <div className="custom-search-input Custom-search-input-Old-leads">
                      <input
                        name="Search"
                        placeholder="Search..."
                        type="text"
                        value={searchText}
                        autoComplete="off"
                        onChange={onSearchTextChange}
                        ref={searchInputRef}
                      />
                      <div className="search-icon-wrapper">
                        <span className="svg-icon">
                          <SVG src={toAbsoluteUrl("/media/svg/icons/custom/search-outline.svg")} />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {isSearchAllow && (
                  <>
                    <div className="form-group mr-3 mb-4" style={{ width: "160px" }}>
                      <div
                        className="custom-date-picker-input"
                        ref={fromDateContainerRef}
                      >
                        <DatePicker
                          selected={parseDateFromAPI(fromDateFilter)}
                          onChange={handleFromDateChange}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="From Date"
                          maxDate={parseDateFromAPI(toDateFilter) || new Date()}
                          isClearable
                          wrapperClassName="date-picker-wrapper"
                        />
                        {!fromDateFilter && (
                          <div className="calendar-icon-wrapper">
                            <span className="svg-icon">
                              <SVG src={toAbsoluteUrl("/media/svg/icons/custom/calendar.svg")} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group mr-3 mb-4" style={{ width: "160px" }}>
                      <div
                        className="custom-date-picker-input"
                        ref={toDateContainerRef}
                      >
                        <DatePicker
                          selected={parseDateFromAPI(toDateFilter)}
                          onChange={handleToDateChange}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="To Date"
                          minDate={parseDateFromAPI(fromDateFilter)}
                          maxDate={new Date()}
                          isClearable
                          wrapperClassName="date-picker-wrapper"
                        />
                        {!toDateFilter && (
                          <div className="calendar-icon-wrapper">
                            <span className="svg-icon">
                              <SVG src={toAbsoluteUrl("/media/svg/icons/custom/calendar.svg")} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group mr-3 mb-4 p-0" style={{ width: "150px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="All Leads"
                    className="custom-react-select"
                    options={leadTypeOptions}
                    onChange={changeLeadType}
                    value={defaultLeadType}
                  />
                </div>

                <div className="form-group mr-3 mb-4 p-0" style={{ width: "180px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="All Outcomes"
                    className="custom-react-select"
                    options={callOutcomeOptions}
                    onChange={changeCallOutcomeFilter}
                    value={defaultCallOutcome}
                  />
                </div>

                {showAllCreatedByFilter && (
                  <div className="form-group mr-3 mb-4 p-0" style={{ width: "180px" }}>
                    <Select
                      classNamePrefix="react-select"
                      placeholder="All Created By"
                      className="custom-react-select"
                      options={createdByOptions}
                      onChange={changeCreatedByFilter}
                      value={defaultCreatedBy}
                      isLoading={isLoadingCreatedByUsers}
                    />
                  </div>
                )}

                <div className="form-group mr-3 mb-4 p-0" style={{ width: "180px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="All Companies"
                    className="custom-react-select"
                    options={companyNameOptions}
                    onChange={changeCompanyNameFilter}
                    value={defaultCompanyName}
                  />
                </div>

                {isImportAllow && (
                  <>

                    <div className="form-group mr-3 mb-4">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        disabled={isImporting}
                      />
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="import-tooltip">{isImporting ? "Importing..." : "Import Data"}</Tooltip>}
                      >
                        <button
                          type="button"
                          className="custom-action-button"
                          onClick={handleImportClick}
                          disabled={isImporting}
                          style={{ minWidth: "40px", padding: "8px" }}
                        >
                          <span className="svg-icon svg-icon-primary">
                            <SVG src={toAbsoluteUrl("/media/svg/icons/Files/File-import.svg")} height={20} width={20} />
                          </span>
                        </button>
                      </OverlayTrigger>
                    </div>
                    <div className="form-group mb-4">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="sample-file-tooltip">Download Sample File</Tooltip>}
                      >
                        <button
                          type="button"
                          className="custom-action-button"
                          onClick={handleDownloadSample}
                          style={{ minWidth: "40px", padding: "8px" }}
                        >
                          <span className="svg-icon svg-icon-primary">
                            <SVG src={toAbsoluteUrl("/media/svg/icons/Files/File-download.svg")} height={20} width={20} />
                          </span>
                        </button>
                      </OverlayTrigger>
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>
        </CardHeaderToolbar>
      </CardHeader>
      <NewLeadsTable />
      <Modal show={!!importErrors} onHide={() => setImportErrors(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Import Errors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importErrors?.message && <p className="text-danger font-weight-bold">{importErrors.message}</p>}

          {importErrors?.fileDuplicates?.length > 0 && (
            <>
              <h6 className="mt-4">Duplicate Records in Uploaded Sheet ({importErrors.fileDuplicates.length})</h6>
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Sheet Rows</th>
                    <th>Client Names</th>
                    <th>Contact Number</th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors.fileDuplicates.map((dup) => (
                    <tr key={dup.ContactNumber}>
                      <td>{dup.rows.join(", ")}</td>
                      <td>{dup.entries.map((e) => e.ClientName || "-").join(", ")}</td>
                      <td>{dup.ContactNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          {importErrors?.dbDuplicates?.length > 0 && (
            <>
              <h6 className="mt-4">Records Already Exist in the System/Already exists in New Data Call ({importErrors.dbDuplicates.length})</h6>
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Sheet Rows</th>
                    <th>Existing Client Name</th>
                    <th>Contact Number</th>
                    <th>Company</th>
                    <th>Batch Name</th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors.dbDuplicates.map((dup) => (
                    <tr key={dup.existingLead?._id || dup.ContactNumber}>
                      <td>{dup.rows.join(", ")}</td>
                      <td>{dup.existingLead?.ClientName || "-"}</td>
                      <td>{dup.ContactNumber}</td>
                      <td>{dup.existingLead?.CompanyName || "-"}</td>
                      <td>{dup.existingLead?.BatchName || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          {importErrors?.missingRequiredRows?.length > 0 && (
            <>
              <h6 className="mt-4">Required field missing ({importErrors.missingRequiredRows.length})</h6>
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Sheet Rows</th>
                    <th>Client Name</th>
                    <th>Contact Number</th>
                    <th>Missing Fields</th>
                  </tr>
                </thead>
                <tbody>
                  {importErrors.missingRequiredRows.map((item) => (
                    <tr key={item.row}>
                      <td>{item.row}</td>
                      <td className={item.ClientName ? "" : "text-center"}>{item.ClientName || "-"}</td>
                      <td className={item.ContactNumber ? "" : "text-center"}>{item.ContactNumber || "-"}</td>
                      <td>{item.missingFields.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-blue" onClick={() => setImportErrors(null)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default NewDataCall;

